import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import CfLoader from "../../common/CfLoader/CfLoader";
import toast from "react-hot-toast";
import Toast from "../../common/Toaster/Toaster";
import {
  DownloadIcon,
  CreditCard,
  User,
  FileText,
  Check,
  Clock,
  X,
  AlertTriangle,
} from "lucide-react";
import RefreshIcon from "../../resources/icons/refresh-icon.svg";
import VerificationNavbar from "../CombinedValidate/VerificationNavbar";
import ProgressStepper from "../CombinedValidate/ProgressStepper";
import { downloadPDF } from "../../service/DownloadPDF";
import isPdfFile from "../../utils/isPdfFile";
import { useTranslation } from "react-i18next";
import { useBrowserWarningNavigate } from "../../hooks/useBrowserWarningNavigate";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";
import {
  buildVcVerificationDisplayData,
  formatVerificationCreatedAt,
  getDisplayCredentialId,
  isApostilleDisplayLabel,
  isCredentialSubjectEmpty,
} from "../../utils/vcVerificationDisplay";
import { isCodeNameObject } from "../../utils/fieldValueDisplay";
import CodeNameValue from "../../components/CodeNameValue/CodeNameValue";
import ServiceUnavailableModal from "../../common/ServiceUnavailableModal/ServiceUnavailableModal";
import { currentTenant } from "../../configs/tenantConfig";
import "../../variables.css";

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const normalizeStatusKey = (status) => {
  if (!status || typeof status !== "string") return "";
  return status.toLowerCase().replace(/[\s-_]+/g, "");
};

const isSameStatus = (left, right) =>
  normalizeStatusKey(left) === normalizeStatusKey(right);

const Summary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const isVCFlow = location.pathname.includes("/vc/");
  const isLegacyFlow = location.pathname.includes("/legacy/");
  const isOCRFlow = location.pathname.includes("/ocr/");

  const [activeSection, setActiveSection] = useState(
    isOCRFlow ? "vc" : isVCFlow ? "vc" : "legacy",
  );
  const [currentStep, setCurrentStep] = useState(
    isOCRFlow ? 3 : isVCFlow ? 2 : 3,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [certificateData, setCertificateData] = useState({});
  const [summaryData, setSummaryData] = useState({});
  const [verificationData, setVerificationData] = useState({});
  const [uploadedImageLoading, setUploadedImageLoading] = useState(true);

  const [showServiceUnavailableModal, setShowServiceUnavailableModal] =
    useState(false);
  const [isPaymentDetailsExpanded, setIsPaymentDetailsExpanded] =
    useState(true);

  const collapsibleStyles = `
    .collapsible-wrapper {
      display: grid;
      grid-template-rows: ${isPaymentDetailsExpanded ? "1fr" : "0fr"};
      transition: grid-template-rows 0.3s ease-in-out;
    }
    
    .collapsible-content {
      overflow: hidden;
    }
    
    .chevron-icon {
      transition: transform 0.3s ease-in-out;
      transform: ${
        isPaymentDetailsExpanded ? "rotate(180deg)" : "rotate(0deg)"
      };
    }
  `;

  const transaction_id = params.transaction_id;
  const order_id = params.order_id;
  const stateData = location.state || {};

  const steps = {
    vc: [t("verify:upload"), t("verify:payment"), t("verify:summary")],
    ocr: [
      t("verify:ocr"),
      t("verify:form"),
      t("verify:payment"),
      t("verify:summary"),
    ],
    legacy: [
      t("verify:select_certificate"),
      t("verify:details"),
      t("verify:payment"),
      t("verify:summary"),
    ],
  };

  const currentSteps = isOCRFlow
    ? steps.ocr
    : isVCFlow
      ? steps.vc
      : steps.legacy;

  const stablePdfUrl = React.useMemo(() => {
    const pdfFile =
      summaryData.uploadedCertificate || certificateData.uploadedFile?.url;
    if (!pdfFile) return null;
    return pdfFile;
  }, [summaryData.uploadedCertificate, certificateData.uploadedFile?.url]);

  const getTranslatedLabel = (key) => {
    const labelMap = {
      certificate_id: t("verify:certificate_id"),
      programme_name: t("verify:programme_name"),
      issuer: t("verify:issuer"),
      candidate_name: t("verify:candidate_name"),
      candidate_email: t("verify:candidate_email"),
    };
    return labelMap[key] || formatFieldName(key);
  };

  // Function to fetch payment/verification status from API
  const checkPaymentStatus = async (
    verificationLogId,
    orderId = null,
    paymentId = null,
    paymentSignature = null,
    isRefresh = false,
    updatePayment = false,
  ) => {
    if (!isRefresh) {
      setIsLoading(true);
    }

    try {
      let payload, endpoint;

      if (isVCFlow) {
        payload = {
          verification_log_id: verificationLogId,
        };
        if (orderId) payload.order_id = orderId;
        if (paymentId) payload.payment_id = paymentId;
        if (paymentSignature) payload.payment_signature = paymentSignature;

        if (updatePayment) {
          payload.payment_update = "payment_update";
        }

        endpoint = EntityConstants.PAYMENT_STATUS;
      } else {
        // OCR and Legacy flow use the same endpoint
        payload = {
          verification_log_id: verificationLogId,
        };

        if (orderId) payload.order_id = orderId;
        if (paymentId) payload.payment_id = paymentId;
        if (paymentSignature) payload.payment_signature = paymentSignature;

        if (updatePayment) {
          payload.payment_update = "payment_update";
        }

        endpoint = EntityConstants.LEGACY_PAYMENT_STATUS;
      }

      const response = await POST(endpoint, payload);

      if (response.ok) {
        const data = await response.json();

        let responseData;
        if (isVCFlow) {
          responseData = data;
        } else {
          responseData = data.data || data;
        }

        // Prefer application_status; fall back to legacy `status` so existing flows keep working
        setSummaryData((prev) => {
          const nextStatus =
            responseData.application_status ||
            responseData.status ||
            prev.status;
          const nextVcStatus = hasOwn(responseData, "vc_status")
            ? responseData.vc_status
            : prev.vcStatus;
          const statusChanged = !isSameStatus(nextStatus, prev.status);

          return {
            ...prev,
            status: nextStatus,
            applicationStatusMessage: hasOwn(
              responseData,
              "application_status_message",
            )
              ? responseData.application_status_message
              : statusChanged
                ? null
                : prev.applicationStatusMessage,
            vcStatus: nextVcStatus,
            paymentStatus: responseData.payment_status,
            statusPdfUrl: responseData.pdf_url,
            comments: responseData.comments,
            verificationTotalAmount: responseData.total_verification_cost,
            verificationAmount:
              responseData.verification_cost?.verification_fee,
            platformFee: responseData.verification_cost?.platform_fee,
            gst: responseData.verification_cost?.gst_fee,
            igst: responseData.verification_cost?.igst_fee,
            sgst: responseData.verification_cost?.sgst_fee,
            cgst: responseData.verification_cost?.cgst_fee,
            gstPercentage: responseData.verification_cost?.gst_percentage,
            igstPercentage: responseData.verification_cost?.igst_percentage,
            sgstPercentage: responseData.verification_cost?.sgst_percentage,
            cgstPercentage: responseData.verification_cost?.cgst_percentage,
            uploadedCertificate: responseData.uploaded_document,
            programmeName: responseData.programme_name,
            verificationLogId:
              responseData.verification_log_id || verificationLogId,
            issuerOrganisation:
              responseData.org_name || responseData.issuer_name,
            issuerName: responseData.issuer_name,
            orderId: responseData.order_id,
            paymentId: responseData.payment_id,
            documentType: responseData.document_type,
            modeOfVerification: responseData.mode_of_verification,
            issuerLogo: responseData.issuer_logo,
            verificationForm: responseData.verification_form,
            displayCredentialId: isVCFlow && responseData?.credential_subject !== undefined
              ? getDisplayCredentialId(responseData) || prev.displayCredentialId || null
              : prev.displayCredentialId,
            createdAt: responseData.created_at || prev.createdAt || null,
            certificateId:
              responseData.certificate_id || prev.certificateId || null,
          };
        });

        if (isVCFlow) {
          if (responseData?.credential_subject !== undefined && isCredentialSubjectEmpty(responseData.credential_subject)) {
            setVerificationData({});
            setShowServiceUnavailableModal(true);
          } else {
            setShowServiceUnavailableModal(false);
            setVerificationData(
              buildVcVerificationDisplayData(
                responseData,
                t("verify:not_available"),
              ),
            );
          }
        } else if (isOCRFlow && responseData.verification_data) {
          setVerificationData(responseData.verification_data);
        } else if (responseData.verification_data) {
          setVerificationData(responseData.verification_data);
        } else {
          toast.error(t("verify:failed_fetch_status"));
        }

        return responseData;
      } else {
        console.error("Error in fetching payment status:", response.status);
        toast.error(t("verify:failed_fetch_status"));
        return null;
      }
    } catch (error) {
      console.error("Error in fetching payment status:", error);
      toast.error(t("verify:failed_fetch_status"));
      return null;
    } finally {
      if (!isRefresh) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (
        stateData.transaction_verification_log_id &&
        Object.keys(stateData).length === 1
      ) {
        const apiData = await checkPaymentStatus(
          stateData.transaction_verification_log_id,
        );

        if (!apiData) {
          if (isVCFlow || isOCRFlow) {
            navigate("/verifier/validate");
          } else {
            navigate("/verifier/legacy/certificate-details");
          }
        }
      }
      // Handle normal flow with complete state data
      if (stateData && Object.keys(stateData).length > 0) {
        if (isOCRFlow) {
          setSummaryData({
            verificationLogId: stateData.verificationLogId || transaction_id,
            orderId: stateData.orderId || order_id,
            issuerName: stateData.issuerName,
            programmeName: stateData.programmeName,
            uploadedCertificate:
              stateData.uploadedDocument || stateData.uploadedCertificate,
            verificationTotalAmount: stateData.verificationTotalAmount,
            verificationAmount: stateData.verificationAmount,
            platformFee: stateData.platformFee,
            gst: stateData.gst,
            gstPercentage: stateData.gstPercentage,
            issuerOrganisation: stateData.issuerOrganisation,
            status: stateData.application_status || stateData.status,
            applicationStatusMessage: stateData.application_status_message,
            vcStatus: stateData.vc_status,
            paymentStatus: stateData.paymentStatus,
            statusPdfUrl: stateData.pdf_url,
            certificateId: stateData.certificateId,
            recipientName: stateData.recipientName,
            recipientEmail: stateData.recipientEmail,
            comments: stateData.comments,
            documentType: "OCR",
            paymentId: stateData.paymentId,
            paymentSignature: stateData.paymentSignature,
            ocrData: stateData.ocrData,
          });

          if (stateData.ocrData) {
            setVerificationData(stateData.ocrData);
          }
        } else if (isVCFlow) {
          setSummaryData({
            verificationLogId: stateData.verificationLogId || transaction_id,
            orderId: stateData.orderId || order_id,
            issuerName: stateData.issuerName,
            programmeName: stateData.programmeName,
            uploadedCertificate:
              stateData.uploadedDocument || stateData.uploadedCertificate,
            verificationTotalAmount: stateData.verificationTotalAmount,
            verificationAmount: stateData.verificationAmount,
            platformFee: stateData.platformFee,
            gst: stateData.gst,
            gstPercentage: stateData.gstPercentage,
            issuerOrganisation: stateData.issuerOrganisation,
            status:
              stateData.application_status ||
              stateData.status ||
              stateData.transaction_verification_status ||
              stateData.underReviewStatus,
            applicationStatusMessage: stateData.application_status_message,
            vcStatus: stateData.vc_status,
            paymentStatus: stateData.paymentStatus,
            statusPdfUrl: stateData.pdf_url,
            certificateId:
              stateData.certificateId || stateData.transaction_certificate_id,
            recipientName:
              stateData.recipient_name || stateData.underReviewCandidateName,
            recipientEmail:
              stateData.recipient_email || stateData.underReviewCandidateEmail,
            comments: stateData.reviewcomments || stateData.comments,
            documentType: stateData.document_type,
            paymentId: stateData.paymentId || stateData.payment_id,
            paymentSignature: stateData.paymentSignature || stateData.signature,
            displayCredentialId: stateData.credential_subject !== undefined
              ? getDisplayCredentialId({
                  credential_subject: stateData.credential_subject,
                  certificate_id:
                    stateData.certificateId || stateData.transaction_certificate_id,
                  certificateId:
                    stateData.certificateId || stateData.transaction_certificate_id,
                })
              : null,
            createdAt: stateData.credential_subject !== undefined
              ? stateData.created_at || stateData.createdAt || null
              : null,
          });

          if (stateData.credential_subject !== undefined && isCredentialSubjectEmpty(stateData.credential_subject)) {
            setVerificationData({});
            setShowServiceUnavailableModal(true);
          } else {
            setShowServiceUnavailableModal(false);
            setVerificationData(
              buildVcVerificationDisplayData(
                {
                  credential_subject: stateData.credential_subject,
                  certificate_id:
                    stateData.certificateId ||
                    stateData.transaction_certificate_id,
                  programme_name: stateData.programmeName,
                  issuer_name: stateData.issuerName,
                  recipient_name:
                    stateData.recipient_name ||
                    stateData.underReviewCandidateName,
                  recipient_email:
                    stateData.recipient_email ||
                    stateData.underReviewCandidateEmail,
                },
                t("verify:not_available"),
              ),
            );
          }
        } else if (stateData.apiResponse) {
          const apiData = stateData.apiResponse;
          setSummaryData({
            paymentStatus: apiData.payment_status,
            issuerName: apiData.issuer_name,
            programmeName: apiData.programme_name,
            uploadedCertificate: apiData.uploaded_document,
            verificationTotalAmount: apiData.total_verification_cost,
            verificationAmount: apiData.verification_cost?.verification_fee,
            platformFee: apiData.verification_cost?.platform_fee,
            gst: apiData.verification_cost?.gst_fee,
            sgst: apiData.verification_cost?.sgst_fee,
            cgst: apiData.verification_cost?.cgst_fee,
            igst: apiData.verification_cost?.igst_fee,
            gstPercentage: apiData.verification_cost?.gst_percentage,
            sgstPercentage: apiData.verification_cost?.sgst_percentage,
            cgstPercentage: apiData.verification_cost?.cgst_percentage,
            igstPercentage: apiData.verification_cost?.igst_percentage,
            issuerOrganisation: apiData.org_name,
            status: apiData.application_status || apiData.status,
            applicationStatusMessage: apiData.application_status_message,
            vcStatus: apiData.vc_status,
            verificationLogId: apiData.verification_log_id || transaction_id,
            orderId: apiData.order_id,
            comments: apiData.comments,
            documentType: apiData.document_type,
            modeOfVerification: apiData.mode_of_verification,
            issuerLogo: apiData.issuer_logo,
            verificationForm: apiData.verification_form,
          });

          if (apiData.verification_data) {
            setVerificationData(apiData.verification_data);
          }
        } else {
          setCertificateData({
            postIssuerName: stateData.postIssuerName,
            postIssuerCode: stateData.postIssuerCode,
            postProgrammeName: stateData.postProgrammeName,
            postProgrammeCode: stateData.postProgrammeCode,
            postBatchName: stateData.postBatchName,
            postBatchCode: stateData.postBatchCode,
            postCredentialTemplate: stateData.postCredentialTemplate,
            postCertificateName: stateData.postCertificateName,
            isOthersSelected: stateData.isOthersSelected,
            customProgrammeName: stateData.customProgrammeName,
            customBatchName: stateData.customBatchName,
            formData: stateData.formData,
            uploadedFile: stateData.uploadedFile,
            postComments: stateData.postComments,
          });

          setSummaryData({
            paymentId: stateData.paymentId,
            paymentSignature: stateData.paymentSignature,
            verificationLogId: stateData.verificationLogId || transaction_id,
            orderId: stateData.orderId,
            issuerName: stateData.issuerName,
            programmeName: stateData.programmeName,
            uploadedCertificate: stateData.uploadedCertificate,
            verificationTotalAmount: stateData.verificationTotalAmount,
            verificationAmount: stateData.verificationAmount,
            platformFee: stateData.platformFee,
            gst: stateData.gst,
            gstPercentage: stateData.gstPercentage,
            issuerOrganisation: stateData.issuerOrganisation,
            status: stateData.application_status || stateData.status,
            applicationStatusMessage: stateData.application_status_message,
            vcStatus: stateData.vc_status,
            paymentStatus: stateData.paymentStatus,
          });

          if (stateData.verificationData) {
            setVerificationData(stateData.verificationData);
          }
        }
        if (stateData.verificationLogId || transaction_id) {
          checkPaymentStatus(
            stateData.verificationLogId || transaction_id,
            stateData.orderId || order_id,
            stateData.paymentId || stateData.payment_id,
            stateData.paymentSignature || stateData.signature,
            false,
            true,
          );
        }
      } else {
        if (isVCFlow || isOCRFlow) {
          navigate("/verifier/validate");
        } else {
          navigate("/verifier/legacy/certificate-details");
        }
      }
    };

    loadData();
  }, [
    location.state,
    navigate,
    transaction_id,
    order_id,
    isVCFlow,
    isOCRFlow,
    isLegacyFlow,
  ]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === "vc") {
      navigate("/verifier/validate");
    } else if (section === "legacy") {
      navigate("/verifier/validate", {
        state: { showLegacySection: "legacy" },
      });
    }
  };

  const handleRefresh = () => {
    if (summaryData.verificationLogId) {
      checkPaymentStatus(
        summaryData.verificationLogId,
        null,
        null,
        null,
        true,
        false,
      );
    } else {
      toast.error(t("verify:refresh_missing_log_id"));
    }

    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 2000);

    toast.success(t("verify:status_refreshed_success"));
  };

  const downloadInvoice = async (transactionId, type = "pdf") => {
    try {
      const response = await GET(
        `${EntityConstants.DOWNLOAD_INVOICE}/${transactionId}?type=${type}`,
      );

      if (!response.ok) {
        throw new Error(
          `${t("verify:failed_download_invoice_with_error")} ${response.status}`,
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `invoice_${transactionId}.${type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      Toast({
        type: "success",
        title: t("verify:invoice_download_success"),
        message: "",
        domId: "summary-invoice-downloaded-toast",
        toastId: "summary-invoice-downloaded-toast",
      });
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(t("verify:failed_download_invoice"));
    }
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/Id$/i, "ID")
      .replace(/Number$/i, "No.");
  };

  const renderFieldValue = (value, depth = 0) => {
    if (value === null || value === undefined) {
      return t("verify:not_available");
    }

    if (isCodeNameObject(value)) {
      return <CodeNameValue value={value} />;
    }

    if (Array.isArray(value)) {
      return (
        <div className="mt-2 space-y-2">
          {value.map((item, index) => (
            <div key={index} className="border-l-2 border-gray-300 pl-3">
              {isCodeNameObject(item) ? (
                renderFieldValue(item, depth + 1)
              ) : typeof item === "object" && item !== null ? (
                <div className="space-y-2">
                  {Object.entries(item).map(([key, val]) => (
                    <div
                      key={key}
                      className={
                        isCodeNameObject(val)
                          ? "flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start"
                          : "flex justify-between gap-2"
                      }
                    >
                      <span className="text-sm font-medium text-gray-600 shrink-0">
                        {formatFieldName(key)}:
                      </span>
                      <span className="text-sm text-gray-900 text-right flex-1 min-w-0">
                        {renderFieldValue(val, depth + 1)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-gray-900">{String(item)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      return (
        <div className={`${depth > 0 ? "mt-2" : ""} space-y-2`}>
          {Object.entries(value).map(([key, val]) => (
            <div
              key={key}
              className={
                isCodeNameObject(val)
                  ? "flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start"
                  : "flex justify-between gap-2"
              }
            >
              <span className="text-sm font-medium text-gray-600 shrink-0">
                {formatFieldName(key)}:
              </span>
              <span className="text-sm text-gray-900 text-right flex-1 min-w-0">
                {renderFieldValue(val, depth + 1)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return String(value);
  };

  const isInProgressStatus = (status) =>
    normalizeStatusKey(status) === "inprogress";

  const isPendingStatus = (status) => {
    const key = normalizeStatusKey(status);
    return key === "approvalpending" || key === "pending";
  };

  const isWaitingStatus = (status) =>
    isPendingStatus(status) || isInProgressStatus(status);

  const shouldShowVcStatus = (applicationStatus, vcStatus) => {
    if (!vcStatus) return false;
    const key = normalizeStatusKey(applicationStatus);
    return key === "approved" || key === "rejected";
  };

  const getDisplayStatus = (status) => {
    const key = normalizeStatusKey(status);
    switch (key) {
      case "approved":
        return t("verify:status_approved");
      case "valid":
        return t("verify:status_valid");
      case "approvalpending":
      case "pending":
        return t("verify:status_approval_pending");
      case "expired":
        return t("verify:status_expired");
      case "revoked":
        return t("verify:status_revoked");
      case "rejected":
        return t("verify:status_rejected");
      case "inprogress":
        return t("verify:status_in_progress");
      default:
        return status || t("verify:status_unknown");
    }
  };

  const getStatusMessage = (status, apiMessage) => {
    if (apiMessage) return apiMessage;

    const key = normalizeStatusKey(status);
    switch (key) {
      case "approved":
        return t("verify:msg_verification_completed");
      case "approvalpending":
      case "pending":
        return t("verify:msg_request_pending");
      case "expired":
        return t("verify:msg_verification_expired");
      case "revoked":
        return t("verify:msg_verification_revoked");
      case "rejected":
        return t("verify:msg_request_rejected");
      case "inprogress":
        return t("verify:msg_request_in_progress");
      default:
        return t("verify:msg_status_unavailable");
    }
  };

  const statusConfig = {
    approved: {
      icon: Check,
      iconClass: "text-cfSummaryStatusApproved",
      iconWrapClass: "bg-cfSummaryStatusApprovedIconBg",
      badgeClass:
        "text-cfSummaryStatusApproved bg-cfSummaryStatusApprovedSoft border-cfSummaryStatusApprovedBorder",
    },
    pending: {
      icon: Clock,
      iconClass: "text-cfSummaryStatusPending",
      iconWrapClass: "bg-cfSummaryStatusPendingIconBg",
      badgeClass:
        "text-cfSummaryStatusPending bg-cfSummaryStatusPendingSoft border-cfSummaryStatusPendingBorder",
    },
    revoked: {
      icon: X,
      iconClass: "text-cfSummaryStatusRejected",
      iconWrapClass: "bg-cfSummaryStatusRejectedIconBg",
      badgeClass:
        "text-cfSummaryStatusRejected bg-cfSummaryStatusRejectedSoft border-cfSummaryStatusRejectedBorder",
    },
    expired: {
      icon: AlertTriangle,
      iconClass: "text-cfSummaryStatusExpired",
      iconWrapClass: "bg-cfSummaryStatusExpiredIconBg",
      badgeClass:
        "text-cfSummaryStatusExpired bg-cfSummaryStatusExpiredSoft border-cfSummaryStatusExpiredBorder",
    },
    rejected: {
      icon: X,
      iconClass: "text-cfSummaryStatusRejected",
      iconWrapClass: "bg-cfSummaryStatusRejectedIconBg",
      badgeClass:
        "text-cfSummaryStatusRejected bg-cfSummaryStatusRejectedSoft border-cfSummaryStatusRejectedBorder",
    },
  };

  const getStatusConfig = (status) => {
    const key = normalizeStatusKey(status);
    // VC "Valid" uses the same positive/green treatment as Approved
    if (key === "approved" || key === "valid") return statusConfig.approved;
    if (
      key === "approvalpending" ||
      key === "pending" ||
      key === "inprogress"
    ) {
      return statusConfig.pending;
    }
    if (key === "revoked") return statusConfig.revoked;
    if (key === "expired") return statusConfig.expired;
    if (key === "rejected") return statusConfig.rejected;
    return statusConfig.pending;
  };

  const applicationStatus = summaryData.status;
  const showVcStatus = shouldShowVcStatus(
    applicationStatus,
    summaryData.vcStatus,
  );
  const applicationStatusConfig = getStatusConfig(applicationStatus);
  const ApplicationStatusIcon = applicationStatusConfig.icon;
  const vcStatusConfig = showVcStatus
    ? getStatusConfig(summaryData.vcStatus)
    : null;
  const VcStatusIcon = vcStatusConfig?.icon;
  const isWaitingOnApplication = isWaitingStatus(applicationStatus);
  const isWaitingOnVc = showVcStatus && isWaitingStatus(summaryData.vcStatus);
  const canRefreshStatus = isWaitingOnApplication || isWaitingOnVc;
  const canDownloadReport =
    !isWaitingOnApplication && Boolean(summaryData.statusPdfUrl);

  const shouldShowPaymentDetails =
    summaryData.verificationTotalAmount ||
    summaryData.verificationAmount ||
    summaryData.platformFee ||
    summaryData.gst;

  const formattedCreatedAt = formatVerificationCreatedAt(summaryData.createdAt);
  const showVcStatusMeta =
    currentTenant === "stp" && isVCFlow && summaryData.displayCredentialId;

  useBrowserWarningNavigate(t("verify:unsaved_changes_warning"));

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <>
      <style>{collapsibleStyles}</style>
      <div className="min-h-screen pt-[6rem] pb-[2rem] bg-white px-4">
        <VerificationNavbar
          activeSection={activeSection}
          handleSectionChange={handleSectionChange}
        />
        <ProgressStepper steps={currentSteps} currentStep={currentStep} />

        <div className="space-y-4">
          <div className="w-full flex flex-col md:flex-row justify-between border border-black rounded-xl p-4 shadow-sm">
            <div className="w-full md:w-[50%] flex flex-col gap-4">
              <div className="flex gap-2 items-center">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${applicationStatusConfig.iconWrapClass}`}
                >
                  <ApplicationStatusIcon
                    strokeWidth={2.5}
                    className={`w-5 h-5 ${applicationStatusConfig.iconClass}`}
                  />
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="text-lg font-semibold text-gray-900">
                    {t("verify:application_status")}:{" "}
                    <span id="summary-application-status-value">
                      {getDisplayStatus(applicationStatus)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 leading-relaxed">
                    {getStatusMessage(
                      applicationStatus,
                      summaryData.applicationStatusMessage,
                    )}
                  </div>
                  {formattedCreatedAt && (
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium text-gray-700">
                        {t("verify:created_at")}:
                      </span>{" "}
                      {formattedCreatedAt}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {showVcStatusMeta && (
              <div className="flex flex-col items-start md:items-end gap-1 px-4 py-3 bg-blue-500/5 border border-blue-500/20 rounded-lg min-w-[12rem]">
                <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">
                  {t("verify:credential_id")}
                </span>
                <span className="text-base md:text-lg font-semibold text-gray-900 break-all text-left md:text-right">
                  {summaryData.displayCredentialId}
                </span>
              </div>
            )}

            {canRefreshStatus && (
              <div
                id="summary-refresh-status"
                className="flex gap-2 p-2 border border-black rounded-lg mt-2 md:!mt-0 h-[2.5rem] items-center cursor-pointer w-fit"
                onClick={handleRefresh}
              >
                <div>
                  <img
                    src={RefreshIcon}
                    height={25}
                    width={25}
                    alt="Refresh"
                    className={`cursor-pointer transform transition-transform duration-[2000ms] ${
                      isRefreshing ? "rotate-[360deg]" : ""
                    }`}
                  />
                </div>
                <div className="text-gray-900 font-medium pr-1">
                  {t("verify:refresh_status")}
                </div>
              </div>
            )}

            {canDownloadReport && (
              <button
                id="summary-download-report-button"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadPDF(
                    summaryData.statusPdfUrl,
                    summaryData.programmeName,
                    summaryData.verificationLogId,
                    summaryData.issuerOrganisation || summaryData.issuerName,
                  );
                }}
                className="flex mt-[1rem] sm:mt-0 items-center gap-2 px-3 h-[2.6rem] rounded-[8px] bg-cfPrimaryButton hover:cfPrimaryButtonHover text-white font-medium shadow-md hover:bg-primary transition-colors duration-200 text-sm sm:text-base"
              >
                <DownloadIcon className="w-5 h-5 shrink-0" />
                <span className="hidden sm:inline">
                  {t("verify:download_verification_report")}
                </span>
                <span className="sm:hidden">{t("verify:download_report")}</span>
              </button>
            )}
          </div>

          <div className="w-full flex flex-col md:flex-row justify-between mt-3 gap-3">
            {!showServiceUnavailableModal && (
            <>
            <div
              className={`flex flex-col ${
                summaryData.uploadedCertificate ||
                certificateData.uploadedFile?.url
                  ? "w-full md:w-[50%]"
                  : "w-full"
              } border border-black rounded-xl p-4 shadow-sm`}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 bg-cfSummaryCandidateIconBg rounded-lg flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-cfSummaryCandidateIcon" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {isOCRFlow
                        ? t("verify:extracted_details")
                        : t("verify:candidate_details")}
                    </h3>
                  </div>
                </div>

                {showVcStatus && vcStatusConfig && (
                  <div className="flex flex-col items-end gap-1 shrink-0 max-w-[55%]">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {t("verify:credential_status")}
                    </span>
                    <div
                      id="summary-credential-status-badge"
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold border transition-colors ${vcStatusConfig.badgeClass}`}
                    >
                      <VcStatusIcon
                        className={`w-3.5 h-3.5 ${vcStatusConfig.iconClass}`}
                        strokeWidth={2.5}
                      />
                      {getDisplayStatus(summaryData.vcStatus)}
                    </div>
                  </div>
                )}
              </div>

              {Object.keys(verificationData).length > 0 ? (
                <div className="relative">
                  <div className="space-y-4 max-h-[500px] overflow-y-auto pb-2 [mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]">
                    {Object.entries(verificationData)
                      .filter(
                        ([fieldName, fieldValue]) =>
                          !(
                            fieldName === "batch_name" &&
                            (fieldValue === "" || fieldValue === null)
                          ),
                      )
                      .map(([fieldName, fieldValue]) => {
                        if (fieldName.startsWith("__section__")) {
                          const i18nKey = fieldName.replace("__section__", "");
                          return (
                            <div key={fieldName} className="pt-1 pb-2">
                              <h4 className="text-base font-bold text-gray-800 italic">
                                {t(`verify:${i18nKey}`)}
                              </h4>
                            </div>
                          );
                        }

                        if (fieldName.startsWith("__pair__")) {
                          const { left, right } = fieldValue;
                          const renderPreview = (url) => {
                            if (!url) {
                              return (
                                <span className="text-sm text-gray-400">
                                  {t("verify:not_available")}
                                </span>
                              );
                            }
                            if (isPdfFile(url)) {
                              return (
                                <TwoPagePdfViewer
                                  pdfUrl={url}
                                  showZoom={false}
                                  heightClassName="max-h-[200px]"
                                />
                              );
                            }
                            return (
                              <img
                                src={url}
                                alt="document preview"
                                className="w-full object-contain rounded border border-gray-200 max-h-[200px]"
                              />
                            );
                          };
                          return (
                            <div
                              key={fieldName}
                              className="border-b border-gray-200 pb-4"
                            >
                              <div className="flex gap-4">
                                <div className="flex-1 flex flex-col gap-2">
                                  <div className="text-md text-gray-600 font-medium">
                                    {t(`verify:${left.i18nKey}`)}:
                                  </div>
                                  {renderPreview(left.value)}
                                </div>
                                <div className="flex-1 flex flex-col gap-2">
                                  <div className="text-md text-gray-600 font-medium">
                                    {t(`verify:${right.i18nKey}`)}:
                                  </div>
                                  {renderPreview(right.value)}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        let displayLabel;

                        if (isApostilleDisplayLabel(fieldName)) {
                          displayLabel = t(`verify:${fieldName}`);
                        } else if (isLegacyFlow || isOCRFlow) {
                          displayLabel = formatFieldName(fieldName);
                        } else {
                          displayLabel = getTranslatedLabel(fieldName);
                        }

                        if (summaryData.verificationForm) {
                          const components =
                            summaryData.verificationForm.verification_fields
                              ?.components ||
                            summaryData.verificationForm.components ||
                            [];
                          const matchingComponent = components.find(
                            (comp) => comp.key === fieldName,
                          );

                          if (matchingComponent && matchingComponent.label) {
                            displayLabel = matchingComponent.label;
                          }
                        }

                        const useStackedLayout =
                          isApostilleDisplayLabel(fieldName);

                        return (
                          <div
                            key={fieldName}
                            className="border-b border-gray-200 pb-4"
                          >
                            <div
                              className={
                                useStackedLayout
                                  ? "flex flex-col gap-2"
                                  : "flex justify-between items-start"
                              }
                            >
                              <div
                                className={`text-md text-gray-600 font-medium ${
                                  useStackedLayout
                                    ? "leading-snug"
                                    : "flex-shrink-0 mr-4"
                                }`}
                              >
                                {displayLabel}:
                              </div>
                              <div
                                className={`text-md text-gray-900 font-normal flex-1 ${
                                  useStackedLayout ? "" : "text-right"
                                }`}
                              >
                                {renderFieldValue(fieldValue)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t("verify:no_verification_data")}
                </div>
              )}

              {(summaryData.comments || certificateData.postComments) && (
                <div className="flex justify-between w-full my-3 mt-6">
                  <div className="text-md text-gray-600 font-medium">
                    {t("verify:comments")}:{" "}
                  </div>
                  <div className="w-[60%] md:w-[50%] border p-2 rounded-lg border-gray-900 max-h-[6.813rem] overflow-y-auto text-md text-gray-900 font-normal">
                    {summaryData.comments || certificateData.postComments}
                  </div>
                </div>
              )}

              {Object.keys(verificationData).some((key) =>
                isApostilleDisplayLabel(key),
              ) && (
                <div>
                  <hr className="border-gray-200 mt-4" />
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg flex gap-2 items-start">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-900">
                      <span className="font-semibold">
                        {t("verify:apostille_note_label")}{" "}
                      </span>
                      {t("verify:apostille_note")}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {(summaryData.uploadedCertificate ||
              certificateData.uploadedFile?.url) && (
              <div className="flex flex-col w-full md:w-[50%] border border-black rounded-xl p-4 shadow-sm">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("verify:uploaded_document")}
                    </h3>
                  </div>
                </div>
                <div className="w-full mt-3 lg:w-[100%] bg-gray-200 rounded-lg mb-3 lg:mb-0 flex flex-col order-2 lg:order-1">
                  <div className="w-full h-[400px] rounded-lg overflow-hidden flex justify-center items-center">
                    {isPdfFile(
                      summaryData.uploadedCertificate ||
                        certificateData.uploadedFile?.url,
                      summaryData.documentType,
                    ) ? (
                      <TwoPagePdfViewer
                        pdfUrl={stablePdfUrl}
                        showZoom={false}
                        heightClassName="h-full"
                      />
                    ) : (
                      <img
                        src={
                          summaryData.uploadedCertificate ||
                          certificateData.uploadedFile?.url
                        }
                        alt="Certificate Preview"
                        className={`object-contain w-full h-full ${
                          uploadedImageLoading ? "hidden" : "block"
                        }`}
                        onLoad={() => setUploadedImageLoading(false)}
                        onError={() => setUploadedImageLoading(false)}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </div>

          {shouldShowPaymentDetails && (
            <div className="w-full bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm mt-3">
              {/* Header Section */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col md:flex-row gap-3 md:gap-0 items-start md:items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t("verify:payment_details")}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {t("verify:transaction_summary_invoice")}
                      </p>
                    </div>
                  </div>
                  <button
                    id="summary-download-invoice-button"
                    onClick={() => {
                      downloadInvoice(
                        summaryData.verificationLogId || transaction_id,
                        "pdf",
                      );
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-[8px] bg-cfPrimaryButton text-white font-medium shadow-md hover:bg-primary transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    {t("verify:download_invoice")}
                  </button>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div
                    id="summary-toggle-payment-details"
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer"
                    onClick={() =>
                      setIsPaymentDetailsExpanded(!isPaymentDetailsExpanded)
                    }
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-900">
                          {t("verify:total_amount_paid")}
                        </span>
                      </div>
                      <span className="text-2xl font-bold text-cfPrimaryLight">
                        ₹{(summaryData.verificationTotalAmount || 0) / 100}
                      </span>
                    </div>
                  </div>
                  <div className="collapsible-wrapper">
                    <div className="collapsible-content">
                      {/* Divider */}
                      <div className="relative mt-[1rem]">
                        <hr className="border-gray-200" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-white px-3 text-xs text-gray-500 font-medium">
                            {t("verify:breakdown")}
                          </div>
                        </div>
                      </div>

                      {/* Payment Items */}
                      <div className="space-y-3 mt-4">
                        <div className="flex justify-between items-center py-2 hover:bg-gray-50 rounded-md px-2 transition-colors">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-900">
                              {t("verify:verification_amount")}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            ₹{(summaryData.verificationAmount || 0) / 100}
                          </span>
                        </div>

                        <div className="flex justify-between items-center py-2 hover:bg-gray-50 rounded-md px-2 transition-colors">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-900">
                              {t("verify:platform_fee")}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            ₹{(summaryData.platformFee || 0) / 100}
                          </span>
                        </div>

                        {summaryData.igstPercentage && summaryData.igst && (
                          <div className="flex justify-between items-center py-2 hover:bg-gray-50 rounded-md px-2 transition-colors">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-900">
                                {t("verify:igst")} ({summaryData.igstPercentage}
                                )
                              </span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                              ₹{(summaryData.igst || 0) / 100}
                            </span>
                          </div>
                        )}

                        {summaryData.cgstPercentage && summaryData.cgst && (
                          <div className="flex justify-between items-center py-2 hover:bg-gray-50 rounded-md px-2 transition-colors">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-900">
                                {t("verify:cgst")} ({summaryData.cgstPercentage}
                                )
                              </span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                              ₹{(summaryData.cgst || 0) / 100}
                            </span>
                          </div>
                        )}

                        {summaryData.sgstPercentage && summaryData.sgst && (
                          <div className="flex justify-between items-center py-2 hover:bg-gray-50 rounded-md px-2 transition-colors">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-900">
                                {t("verify:sgst")} ({summaryData.sgstPercentage}
                                )
                              </span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                              ₹{(summaryData.sgst || 0) / 100}
                            </span>
                          </div>
                        )}

                        {summaryData.gstPercentage && summaryData.gst && (
                          <div className="flex justify-between items-center py-2 hover:bg-gray-50 rounded-md px-2 transition-colors">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-900">
                                {t("verify:gst")} ({summaryData.gstPercentage})
                              </span>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">
                              ₹{(summaryData.gst || 0) / 100}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Payment Status */}
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-500">
                              {t("verify:payment_status")}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-green-700 bg-green-100 px-3 py-1 rounded-full">
                            {t("verify:status_completed")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ServiceUnavailableModal
        isOpen={showServiceUnavailableModal}
        onClose={() => {
          setShowServiceUnavailableModal(false);
          if (isVCFlow) {
            navigate("/verifier/validate");
          }
        }}
      />
    </>
  );
};

export default Summary;
